import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ModelLogo, ProviderLogo, providerName } from '@shared/brand';
import { idr } from '@shared/format';
import { Icon } from './ui';
import { modalityOf, priceShort, providerOf, type CatalogModel } from '../catalog';
import '../styles/model-picker.css';

function PriceCell({ model }: { model: CatalogModel }) {
  const p = model.pricing;
  if (!p) return <span className="muted">—</span>;
  const mode = modalityOf(model);
  if (mode === 'image') return <>{idr(p.per_image ?? 0)}<small> / gambar</small></>;
  if (mode === 'video') return <>{idr(p.per_second_720p ?? 0)}<small> / detik</small></>;
  return (
    <>
      {idr(p.input_per_1m ?? 0)}
      <small> input</small>
      <br />
      {idr(p.output_per_1m ?? 0)}
      <small> output</small>
    </>
  );
}

export function ModelPicker({
  models,
  value,
  onChange,
  id,
  disabled = false,
}: {
  models: CatalogModel[];
  value: string;
  onChange: (id: string) => void;
  id?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selected = models.find((m) => m.id === value);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hits = models.filter(
      (m) =>
        !q ||
        m.id.toLowerCase().includes(q) ||
        m.display_name.toLowerCase().includes(q) ||
        providerName(providerOf(m)).toLowerCase().includes(q),
    );
    const map = new Map<string, CatalogModel[]>();
    for (const m of hits) map.set(providerOf(m), [...(map.get(providerOf(m)) ?? []), m]);
    return [...map.entries()].map(([provider, items]) => ({ provider, items }));
  }, [models, query]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const allText = models.every((m) => modalityOf(m) === 'text');

  const close = (refocus = true) => {
    setOpen(false);
    setQuery('');
    if (refocus) triggerRef.current?.focus();
  };

  const choose = (modelId: string) => {
    onChange(modelId);
    close();
  };

  useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, flat.findIndex((m) => m.id === value)));
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onSearchKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(flat.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const hit = flat[active];
      if (hit) choose(hit.id);
    }
  };

  return (
    <div className="mp" ref={rootRef}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={open ? 'mp-trigger open' : 'mp-trigger'}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {selected ? (
          <ModelLogo model={selected.id} family={selected.family} provider={providerOf(selected)} size={30} />
        ) : (
          <span className="logo" style={{ width: 30, height: 30 }} />
        )}
        <span className="mp-text">
          <span className="mp-name">{selected?.display_name ?? 'Pilih model'}</span>
          <span className="mp-sub">
            {selected ? (
              <>
                {providerName(providerOf(selected))} · {priceShort(selected)}
              </>
            ) : (
              `${models.length} model tersedia`
            )}
          </span>
        </span>
        <Icon name="chevron" size={16} className={open ? 'mp-caret up' : 'mp-caret'} />
      </button>

      {open && (
        <div className="mp-pop">
          <div className="search">
            <input
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onSearchKey}
              placeholder="Cari model atau provider"
              aria-label="Cari model"
            />
          </div>
          <div className="mp-list" role="listbox" ref={listRef}>
            {groups.map((group) => (
              <div key={group.provider} className="mp-group">
                <div className="mp-group-head">
                  <ProviderLogo provider={group.provider} size={14} bare />
                  {providerName(group.provider)}
                  <span className="mp-count">{group.items.length}</span>
                </div>
                {group.items.map((m) => {
                  const index = flat.indexOf(m);
                  const cls = ['mp-item', index === active ? 'active' : '', m.id === value ? 'current' : ''].filter(Boolean).join(' ');
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="option"
                      aria-selected={m.id === value}
                      data-i={index}
                      className={cls}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => choose(m.id)}
                    >
                      <ModelLogo model={m.id} family={m.family} provider={providerOf(m)} size={28} />
                      <span className="mp-text">
                        <span className="mp-name">
                          {m.display_name}
                          {m.id === value && <Icon name="check" size={14} className="mp-check" />}
                        </span>
                        <span className="mp-id">{m.id}</span>
                      </span>
                      <span className="mp-price">
                        <PriceCell model={m} />
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
            {flat.length === 0 && <div className="mp-empty">Tidak ada model yang cocok dengan “{query}”.</div>}
          </div>
          <div className="mp-foot">
            <span>
              <kbd>↑</kbd> <kbd>↓</kbd> pilih
            </span>
            <span>
              <kbd>Enter</kbd> pakai
            </span>
            <span>
              <kbd>Esc</kbd> tutup
            </span>
            <span className="grow" />
            {allText && <span>Harga per 1 juta token</span>}
          </div>
        </div>
      )}
    </div>
  );
}
